import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Circle, Clock, Loader2, RotateCcw, TriangleAlert } from 'lucide-react';
import {
  CustomerOrderStatus,
  OrderStatus,
  VENUE_EVENTS,
  rooms,
  type OrderItemEventPayload,
} from '@/lib/shared-types';
import { sortGuestOrdersNewestFirst, toCustomerStatus } from '@/lib/domain';
import { formatDateTime } from '@/lib/i18n';
import { orderStatusColors } from '@/lib/ui-tokens';
import { api } from '@/lib/api';
import { readableInk } from '@/lib/contrast';
import {
  isAwaitingWaiterConfirmation,
  isRunningLate,
  reorderableItems,
  unwrapList,
  type TrackedOrder,
} from '@/lib/guest';
import { useRealtime } from '@/Hooks/useRealtime';
import { useResync } from '@/Hooks/useResync';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FeedbackCard } from '@/components/FeedbackCard';
import { GuestLineList } from '@/components/GuestLineList';
import { WaiterOnTheWay, WaiterOnTheWayBadge } from '@/components/WaiterOnTheWay';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import { addManyToCart } from '@/Redux/Slices/guestSlice';

const JOURNEY: CustomerOrderStatus[] = [
  CustomerOrderStatus.ORDER_RECEIVED,
  CustomerOrderStatus.CONFIRMED,
  CustomerOrderStatus.PREPARING,
  CustomerOrderStatus.PARTIALLY_READY,
  CustomerOrderStatus.READY,
  CustomerOrderStatus.SERVED,
  CustomerOrderStatus.COMPLETED,
];

/** PICKED_UP is the takeaway sibling of SERVED — same step on the journey. */
const stepOf = (status: CustomerOrderStatus): CustomerOrderStatus =>
  status === CustomerOrderStatus.PICKED_UP ? CustomerOrderStatus.SERVED : status;

/** An order the guest can sensibly rate. */
const RATEABLE: CustomerOrderStatus[] = [
  CustomerOrderStatus.SERVED,
  CustomerOrderStatus.PICKED_UP,
  CustomerOrderStatus.COMPLETED,
];

/**
 * The guest sees the live journey, derived from item states.
 *
 * How much of it they see is the venue's call: ETA, per-line status,
 * station name and delay notices each have a switch. Queue position has no
 * switch because it is never shown at all.
 */
export default function Track() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const session = useAppSelector((s) => s.guest.session);
  const [orders, setOrders] = useState<TrackedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  /** Station names only ever arrive over the wire, never from the list. */
  const [stations, setStations] = useState<Record<string, string>>({});

  const transparency = session?.transparency;

  /**
   * The API returns this session's orders oldest first (`createdAt: 'asc'`), and the guest
   * wants the opposite: the round they just placed, not the one they finished an hour ago.
   *
   * Sorted once here, on the way into state, rather than at either render below. The two
   * branches of this screen are different components — the flat line list and the fallback
   * cards — and a sort in only one of them is a screen that reverses itself when a venue
   * flips `showItemStatus`. `sortGuestOrdersNewestFirst` is the same rule `toGuestLines`
   * applies inside `GuestLineList`, so the two agree by construction rather than by luck,
   * and it is the same rule the native app imports.
   */
  const load = useCallback(() => {
    if (!session?.sessionId) return;
    api
      .get('/orders', { params: { sessionId: session.sessionId } })
      .then(({ data }) => setOrders(sortGuestOrdersNewestFirst(unwrapList<TrackedOrder>(data))))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [session?.sessionId]);

  useEffect(load, [load]);

  const guestRooms = session?.sessionId ? [rooms.guest(session.sessionId)] : [];

  const onItemEvent = useCallback((payload: OrderItemEventPayload) => {
    if (payload.stationName && payload.orderItemId) {
      setStations((current) => ({ ...current, [payload.orderItemId]: payload.stationName! }));
    }
    load();
  }, [load]);

  // Item-level updates arrive over the guest room and repaint in place.
  //
  // All FOUR item events, including PICKED_UP. The server emits it, the guest
  // projection maps it to its own customer status, and this screen simply never
  // listened — so a waiter collecting a tray changed nothing the guest could
  // see, and the list jumped from "Confirmed" to "Served" with the step between
  // them invisible.
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_PREPARING, onItemEvent, guestRooms);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_READY, onItemEvent, guestRooms);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_PICKED_UP, onItemEvent, guestRooms);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_SERVED, onItemEvent, guestRooms);
  // Also the event that ENDS the waiter state: at a venue with confirmation on,
  // `order.sent_to_kitchen` is the waiter pressing send at the table, and this
  // refetch is what swaps "your waiter is on the way" for the kitchen journey
  // while the guest is still looking at the screen.
  useRealtime(VENUE_EVENTS.ORDER_SENT_TO_KITCHEN, load, guestRooms);
  // Order-level updates also arrive over the guest room, independent of the item
  // events above. A venue with `showItemStatus` off never emits an `item.*` event
  // at all (see `RealtimeService.onOrderItemEvent`), so without these three the
  // tracker heard `order.sent_to_kitchen` and then nothing — the order could be
  // confirmed, ready and completed on the server while this screen sat frozen on
  // "Confirmed" forever, because it never subscribed to the events that say so.
  useRealtime(VENUE_EVENTS.ORDER_CONFIRMED, load, guestRooms);
  useRealtime(VENUE_EVENTS.ORDER_READY, load, guestRooms);
  useRealtime(VENUE_EVENTS.ORDER_COMPLETED, load, guestRooms);

  // And the same read whenever the guest comes back to a screen that could not
  // hear. This tracker is the surface the whole defect was reported against: a
  // phone put down on the table, PREPARING → READY → SERVED happening without
  // it, and a screen still reading PREPARING when it is picked up again,
  // because a backgrounded browser froze the page and the events above were
  // delivered to nobody. Everything above stays instant while the tab is in
  // front; this is what stops "missed" from meaning "wrong until reloaded".
  useResync(load);

  if (loading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  if (!orders.length) {
    return <p className="py-16 text-center text-sm text-muted-foreground">{t('guest.track.empty')}</p>;
  }

  const now = Date.now();
  const rateable = orders.find((order) =>
    RATEABLE.includes(toCustomerStatus(order.status, order.items ?? [])),
  );

  const reorder = (order: TrackedOrder) => {
    const lines = reorderableItems(order).map((item) => ({
      itemId: item.itemId,
      name: item.name,
      unitPrice: Number(item.unitPrice),
      quantity: Math.max(1, Math.round(Number(item.quantity))),
      ...(item.notes ? { notes: item.notes } : {}),
    }));
    if (!lines.length) return;
    dispatch(addManyToCart(lines));
    toast.success(t('guest.track.reordered'));
  };

  return (
    <div className="space-y-4">
      {/* The flat list needs per-line state to say anything. When a
          venue hides it, the order-level status is the only status that
          exists, so the cards stay: flattening there would leave a list of
          names and prices with nothing to report. */}
      {transparency?.showItemStatus ? (
        <GuestLineList
          orders={orders}
          showStationName={Boolean(transparency.showStationName)}
          showDelayUpdates={Boolean(transparency.showDelayUpdates)}
          showEta={Boolean(transparency.showEta)}
          stations={stations}
          now={now}
          onReorder={reorder}
        />
      ) : (
        orders.map((order) => {
        const status = toCustomerStatus(order.status, order.items ?? []);
        const reached = JOURNEY.indexOf(stepOf(status));
        // The server's own flag decides this, not a venue setting read from the
        // session: a session that is missing or stale about the policy must not
        // be able to hide a waiter the API says is already on the way.
        const awaitingWaiter = isAwaitingWaiterConfirmation(order);
        // A delay notice apologises for a kitchen that is behind. Nothing is in
        // a kitchen yet while a waiter is still walking over, so the promised
        // time passing is not something to apologise for — and the notice would
        // contradict the very state above it.
        const late =
          transparency?.showDelayUpdates && !awaitingWaiter && isRunningLate(order, now);
        const cancelled =
          order.status === OrderStatus.CANCELLED || order.status === OrderStatus.VOIDED;

        return (
          <Card key={order.id}>
            <CardContent className="space-y-4 p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">#{order.number}</span>
                {/* The status pill is REPLACED, not accompanied. `CONFIRMED`
                    is the internal name for an order a waiter has not sent yet,
                    and rendering it here says "Confirmed" beside a notice
                    saying nobody has looked at the order — the guest is left to
                    decide which of the two to believe.

                    The fill is otherwise the shared order-status colour,
                    unchanged. Only the ink is measured against it
                    (`lib/contrast`); the hardcoded white it replaced measured
                    2.15:1 on PREPARING. */}
                {awaitingWaiter ? (
                  <WaiterOnTheWayBadge />
                ) : (
                  <span
                    className="rounded-full px-2.5 py-0.5 text-xs font-medium"
                    style={{
                      background: orderStatusColors[status],
                      color: readableInk(orderStatusColors[status]),
                    }}
                  >
                    {t(`order.status.${status}`)}
                  </span>
                )}
              </div>

              {/* An ETA is shown only when the venue allows it AND the
                  order actually carries a promised time. Nothing is estimated
                  here: a made-up ETA is worse than none. */}
              {transparency?.showEta && order.scheduledFor && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {t('guest.track.readyAt', {
                    time: formatDateTime(order.scheduledFor, i18n.language),
                  })}
                </div>
              )}

              {late && (
                <div className="flex items-center gap-2 rounded-lg bg-notice-bg p-2.5 text-sm text-notice">
                  <TriangleAlert className="h-4 w-4 shrink-0" />
                  {t('guest.track.delayed')}
                </div>
              )}

              {awaitingWaiter && <WaiterOnTheWay />}

              {/* The journey is the kitchen's, step for step, so it is not drawn
                  until the kitchen has the order. Drawn early it puts a spinner
                  on "Confirmed" and lists "Preparing" below it, which is the
                  screen this whole change exists to stop showing: a guest
                  watching a tracker tick along while their order has not left
                  the table. */}
              {!cancelled && !awaitingWaiter && (
                <ol className="space-y-2">
                  {JOURNEY.filter(
                    (step) => step !== CustomerOrderStatus.PARTIALLY_READY || status === step,
                  ).map((step) => {
                    const index = JOURNEY.indexOf(step);
                    const done = index < reached;
                    const active = index === reached;

                    return (
                      <li key={step} className="flex items-center gap-2 text-sm">
                        {done ? (
                          <Check className="h-4 w-4 text-positive" />
                        ) : active ? (
                          <Loader2 className="h-4 w-4 animate-spin text-accent" />
                        ) : (
                          <Circle className="h-4 w-4 text-line-strong" />
                        )}
                        <span className={done || active ? '' : 'text-muted-foreground'}>
                          {t(`order.status.${step}`)}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}


              {/* One tap to have the same again. */}
              {!!reorderableItems(order).length && (
                <Button variant="outline" className="w-full" onClick={() => reorder(order)}>
                  <RotateCcw className="h-4 w-4" />
                  {t('guest.track.reorder')}
                </Button>
              )}
            </CardContent>
          </Card>
        );
        })
      )}

      {rateable && <FeedbackCard />}
    </div>
  );
}
